// src/sim/round/team-switch.ts — P3 role flip + diegetic transition lerp (GAME-08, GAME-10, D-31, D-32).
// P4 Plan 09 bench rotation: shipped as sticky bench (see authoritative comment at L202-210).
// lastBenchedNpcId is preserved unchanged across all team-switches; the only write site is
// world init (composeInitialRoster.pickBenchedNpc) + a future "reset rosters" action.
//
// Invariants:
//   - Pure: returns new values; never mutates inputs.
//   - flipRolesAndReset is invoked by tick() when phase==='ended' AND
//     currentTick >= round.endTick + P3_TEAMSWITCH_DURATION_TICKS.
//   - applyTeamSwitchLerp runs each tick while phase==='ended'; controllers
//     return ZERO_INTENT during this window (D-08/D-32).
//   - Constant-step linear interpolation: position = lerp(lastPosition, teamSwitchTarget, t)
//     where t = (currentTick - startTick) / durationTicks. Non-recursive; step size is
//     uniform across the full 210-tick window.
//
// Source: .planning/phases/03-core-sim-primitives/03-CONTEXT.md D-31, D-32, D-33.

import type { WorldState, Entity, RoundState, Vec2 } from '../../shared/types/world';
import type { GameEvent } from '../../shared/types/events';
import type { MapData } from '../../shared/types/map';
// P7 POL-06 / D-12: round/headstart durations read through the tuning cache
// (./constants are the fallback defaults).
import { getTuning } from '../content/cache';
import { computeSpawnLayout, makeSpawnRng } from './spawn-placement';

/**
 * D-32 / D-08 called at transition active→ended (same tick as the `team-switched`
 * event). Uses the shared computeSpawnLayout to spread the 4 post-flip hunters
 * across the four outer zones and cluster the 4 post-flip hiders at the plinth —
 * the same formation as round 1 (D-08). The per-round RNG salt ensures the
 * hunter→zone assignment is deterministic for a given round yet varies round to
 * round (D-02).
 *
 * Sets `Entity.teamSwitchTarget` to the NEW-round starting position (post-flip
 * role), anchors `lastPosition` to the current (pre-transition) position so the
 * constant-step lerp can compute `t` from a stable start, and sets
 * `inTeamswitchTransition=true`. Does NOT flip role yet — that lands in
 * flipRolesAndReset after the lerp completes.
 *
 * @param entities - current entity map (roles are pre-flip)
 * @param mapData - used for zone polygons and playerSpawn fallback
 * @param rngSeed - WorldState.rngSeed; salted by roundNumber for D-02 variation
 * @param roundNumber - current round number; passed to makeSpawnRng for per-round seeding
 */
export function bindTeamSwitchTargets(
  entities: Record<string, Entity>,
  mapData: Pick<MapData, 'playerSpawn' | 'zones'> | null | undefined,
  rngSeed: number,
  roundNumber: number,
): Record<string, Entity> {
  // Partition by post-flip role (flip of current).
  const postFlipHunterIds: string[] = [];
  const postFlipHiderIds: string[] = [];
  for (const [id, e] of Object.entries(entities)) {
    if (e.role === 'hunter') {
      // Current hunter → post-flip hider
      postFlipHiderIds.push(id);
    } else {
      // Current hider → post-flip hunter
      postFlipHunterIds.push(id);
    }
  }

  // computeSpawnLayout: hunters get 4 outer zones (D-01), hiders cluster at plinth (D-04).
  // makeSpawnRng salts with roundNumber so the zone shuffle varies per round (D-02).
  const layout = computeSpawnLayout({
    hunterIds: postFlipHunterIds,
    hiderIds: postFlipHiderIds,
    zones: mapData?.zones ?? null,
    playerSpawn: mapData?.playerSpawn ?? null,
    rng: makeSpawnRng(rngSeed, roundNumber),
  });

  // Apply: set teamSwitchTarget from layout, anchor lerp start, set transition flag.
  const out: Record<string, Entity> = {};
  for (const [id, e] of Object.entries(entities)) {
    out[id] = {
      ...e,
      teamSwitchTarget: layout[id] ?? { x: 0, y: 0 },
      inTeamswitchTransition: true,
      // Anchor the lerp start at the pre-transition position; applyTeamSwitchLerp
      // reads this each tick so step size stays constant across 210 ticks.
      lastPosition: { x: e.position.x, y: e.position.y },
    };
  }
  return out;
}

/**
 * D-32 per-tick constant-step linear lerp. Uses the formula
 *   t = (currentTick - startTick) / durationTicks   (clamped to [0, 1])
 *   position = lerp(lastPosition, teamSwitchTarget, t)
 * so per-tick step size is uniform across the full window. Clears
 * teamSwitchTarget + inTeamswitchTransition on the tick the lerp completes.
 *
 * @param startTick tick at which the lerp began (the active→ended boundary = round.endTick).
 * @param finishTick startTick + P3_TEAMSWITCH_DURATION_TICKS.
 */
export function applyTeamSwitchLerp(
  entities: Record<string, Entity>,
  currentTick: number,
  startTick: number,
  finishTick: number,
): Record<string, Entity> {
  const out: Record<string, Entity> = {};
  const duration = Math.max(1, finishTick - startTick);
  const tRaw = (currentTick - startTick) / duration;
  const t = Math.min(1, Math.max(0, tRaw));
  for (const [id, e] of Object.entries(entities)) {
    if (!e.inTeamswitchTransition || !e.teamSwitchTarget || !e.lastPosition) {
      // Pass-through unchanged — same reference. (Callers rely on this for
      // the "inert" case; also avoids unnecessary allocation.)
      out[id] = e;
      continue;
    }
    const start = e.lastPosition;
    const target = e.teamSwitchTarget;
    if (currentTick >= finishTick) {
      out[id] = {
        ...e,
        position: { x: target.x, y: target.y },
        teamSwitchTarget: null,
        inTeamswitchTransition: false,
      };
    } else {
      const position: Vec2 = {
        x: start.x + (target.x - start.x) * t,
        y: start.y + (target.y - start.y) * t,
      };
      out[id] = { ...e, position };
    }
  }
  return out;
}

/**
 * D-31 role flip + full reset. Called when currentTick >= round.endTick +
 * P3_TEAMSWITCH_DURATION_TICKS. Increments roundNumber; resets every Entity
 * situational field; preserves characterId + id; torchOn forced false.
 */
export function flipRolesAndReset(
  state: WorldState,
  currentTick: number,
): { state: WorldState; events: GameEvent[] } {
  const nextEntities: Record<string, Entity> = {};
  let totalHiders = 0;
  for (const [id, e] of Object.entries(state.entities)) {
    const newRole = e.role === 'hunter' ? 'hider' : 'hunter';
    if (newRole === 'hider') totalHiders++;
    nextEntities[id] = {
      id: e.id,
      // D-31 GAME-12: characterId preserved verbatim across rounds.
      characterId: e.characterId,
      // Position carried forward from the final lerp tick (already at teamSwitchTarget).
      position: { x: e.position.x, y: e.position.y },
      facing: 0,
      torchOn: false,
      role: newRole,
      status: 'free',
      escortingEntityId: null,
      escortedBy: null,
      hidingSpotId: null,
      floorSpot: null,
      peekEndTick: null,
      peekSuppressedUntilRelease: false,
      stamina: 1,
      recentDropOffTick: null,
      foxguardConditionsStreakStartTick: null,
      lastFoxguardShoutTick: null,
      freedImmunityUntilTick: null,
      inTeamswitchTransition: false,
      lastPosition: null,
      teamSwitchTarget: null,
      // P4 additions: reset AI state at team-switch (D-33).
      fear: 0,
      nextDecideTick: 0,
      checkedHidingSpotIds: [],
    };
  }

  const tuning = getTuning();
  const nextRound: RoundState = {
    phase: 'headstart',
    startTick: currentTick,
    headstartEndTick: currentTick + tuning.headstartDurationTicks,
    endTick: currentTick + tuning.headstartDurationTicks + tuning.roundDurationTicks,
    endReason: null,
    roundNumber: state.round.roundNumber + 1,
    caughtCount: 0,
    totalHiders,
  };

  const events: GameEvent[] = [
    {
      type: 'round-started',
      roundNumber: nextRound.roundNumber,
      seed: state.rngSeed,
      tick: currentTick,
    },
  ];

  // UAT Test 7 correction — sticky bench: a benched NPC stays out for the
  // ENTIRE game until the user explicitly picks new teams via the RoundSetup
  // UI. The previous D-39 anti-consecutive rotation rerolled the bench each
  // round; that was a game-design error (the real family rule is that
  // whoever sits out a round sits out the whole session unless teams are
  // re-chosen). `lastBenchedNpcId` is therefore passed through untouched
  // across team-switch, and the only write site becomes world init (via
  // composeInitialRoster's pickBenchedNpc) + a future "reset rosters" action
  // that re-opens RoundSetup.
  return {
    state: {
      ...state,
      entities: nextEntities,
      round: nextRound,
      // D-33: reset both team blackboards at round-started — AI adaptation is within-round only.
      teamBlackboards: {
        hunter: { entries: [], nextId: 0 },
        hider: { entries: [], nextId: 0 },
      },
      // Sticky bench: preserve state.lastBenchedNpcId unchanged.
      lastBenchedNpcId: state.lastBenchedNpcId ?? null,
    },
    events,
  };
}
